import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  ckConfirm,
  EmptyState,
  Field,
  Sheet,
  Spinner,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type MailingDesign, type TemplateDto } from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { EmailBuilder, EmailPreview } from "./EmailBuilder";
import { emptyDesign, errorMessage, fmtDateTime } from "./mailingUi";

type Draft = { id: string | null; name: string; subject: string; preheader: string; design: MailingDesign };

function TemplateEditor({ draft, onClose, onSaved }: { draft: Draft | null; onClose: () => void; onSaved: () => void }) {
  const toast = useCkToast();
  const [d, setD] = useState<Draft | null>(draft);
  const [saving, setSaving] = useState(false);
  useEffect(() => setD(draft), [draft]);
  if (!d) return null;

  const save = async () => {
    if (!d.name.trim()) {
      toast.error("יש להזין שם לתבנית");
      return;
    }
    setSaving(true);
    try {
      await mailingApi.templates.save(d.id, { name: d.name, subject: d.subject, preheader: d.preheader, design: d.design });
      toast.success("התבנית נשמרה");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onClose={onClose} width={1180} ariaLabel="עריכת תבנית">
      <div className="ml-sheet">
        <div className="ml-sheet__head">
          <b>{d.id ? "עריכת תבנית" : "תבנית חדשה"}</b>
          <div className="ml-row">
            <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
              {saving ? "שומר…" : "שמירה"}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              סגירה
            </Button>
          </div>
        </div>
        <div className="ml-sheet__body ml-stack">
          <div className="ml-grid3">
            <Field label="שם התבנית">
              <TextInput value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} maxLength={200} autoFocus />
            </Field>
            <Field label="נושא ברירת מחדל">
              <TextInput value={d.subject} onChange={(e) => setD({ ...d, subject: e.target.value })} maxLength={300} />
            </Field>
            <Field label="טקסט תצוגה מקדימה">
              <TextInput value={d.preheader} onChange={(e) => setD({ ...d, preheader: e.target.value })} maxLength={300} />
            </Field>
          </div>
          <EmailBuilder design={d.design} onChange={(design) => setD({ ...d, design })} />
        </div>
      </div>
    </Sheet>
  );
}

/** Saved email designs — start a mailing from one, or keep a house style. */
export function MailingTemplates({ onUse }: { onUse: (templateId: string) => void }) {
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [items, setItems] = useState<TemplateDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(() => {
    mailingApi.templates
      .list()
      .then(setItems)
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את התבניות")));
  }, []);
  useEffect(load, [load]);

  const edit = async (t: TemplateDto) => {
    try {
      const full = await mailingApi.templates.get(t.id);
      setDraft({ id: full.id, name: full.name, subject: full.subject ?? "", preheader: full.preheader ?? "", design: full.design?.blocks ? full.design : emptyDesign() });
    } catch (e) {
      toast.error(errorMessage(e, "לא הצלחנו לפתוח את התבנית"));
    }
  };

  const remove = async (t: TemplateDto) => {
    if (!(await ckConfirm({ title: "למחוק את התבנית?", message: `"${t.name}" תימחק. דיוורים שכבר נוצרו ממנה לא ישתנו.`, confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await mailingApi.templates.remove(t.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  return (
    <div className="ml-stack">
      {canManage ? (
        <div className="ml-row ml-row--end">
          <Button icon="plus" onClick={() => setDraft({ id: null, name: "", subject: "", preheader: "", design: emptyDesign() })}>
            תבנית חדשה
          </Button>
        </div>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!items ? (
        <div className="ml-busy">
          <Spinner size={24} />
        </div>
      ) : !items.length ? (
        <EmptyState icon="file" title="עדיין אין תבניות">
          תבנית שומרת עיצוב קבוע (לוגו, צבעים, חתימה) — כדי שכל דיוור יתחיל ממנו.
        </EmptyState>
      ) : (
        <div className="ml-tpl-grid">
          {items.map((t) => (
            <article key={t.id} className="ml-tpl">
              <div className="ml-tpl__thumb" aria-hidden="true">
                <EmailPreview html={t.html} height={600} title={t.name} />
              </div>
              <div className="ml-tpl__meta">
                <b>{t.name}</b>
                <small className="ml-muted">{t.subject || "ללא נושא"} · {fmtDateTime(t.updatedAt)}</small>
              </div>
              {canManage ? (
                <div className="ml-row">
                  <Button size="sm" variant="primary" icon="mail" onClick={() => onUse(t.id)}>
                    דיוור מהתבנית
                  </Button>
                  <Button size="sm" icon="edit" onClick={() => void edit(t)}>
                    עריכה
                  </Button>
                  <Button size="sm" variant="ghost" icon="trash" iconOnly aria-label="מחיקה" onClick={() => void remove(t)} />
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
      <TemplateEditor draft={draft} onClose={() => setDraft(null)} onSaved={load} />
    </div>
  );
}
